"use client";

/**
 * Global Modal Stack Provider
 * 
 * Provides a global modal stack management system that can be used across
 * the entire application. This ensures proper z-index stacking, focus management,
 * and escape key handling for all modals.
 * 
 * Features:
 * - Centralized modal state management
 * - Optional URL synchronization for shareable modal states
 * - Proper z-index stacking with Mantine Modal.Stack
 * - Focus trap and escape key handling
 * - Support for dynamic modal IDs (e.g., list-editor-{entityName}-{entityId}-{fieldId})
 * 
 * @example
 * // Enable URL sync at the app level
 * <GlobalModalStackProvider syncWithUrl urlParamName="modal">
 *   <App />
 * </GlobalModalStackProvider>
 * 
 * // Use in components
 * const { openModal, closeModal, isModalOpen } = useGlobalModalStack();
 * 
 * // Open modal (updates URL if syncWithUrl is enabled)
 * openModal("edit-contact");
 * 
 * // Check if modal is open (reads from URL if syncWithUrl is enabled)
 * const isOpen = isModalOpen("edit-contact");
 * 
 * // Dynamic modal IDs are automatically supported
 * openModal("list-editor-contacts-abc123-tags");
 */

import * as React from "react";
import { Modal, useModalsStack } from "@mantine/core";
import { GlobalModalStackContext } from "./context";
import { GLOBAL_MODAL_IDS } from "./constants";
import type { GlobalModalStackProviderProps, GlobalModalStackContextType } from "./types";
import type { GlobalModalId } from "./types";
import { useModalDataStore } from "./hooks/useModalDataStore";
import { useUrlSync, NO_OP_URL_SYNC } from "./hooks/useUrlSync";
import { useModalActions } from "./hooks/useModalActions";

// Generate a large set of potential dynamic modal IDs to support nested modals
// This allows for dynamic modal IDs without needing to re-initialize the modal stack
const generateDynamicModalIds = (): string[] => {
  const dynamicIds: string[] = [];
  
  // Generate list-editor modal IDs for all entity types and common field names
  const entities = ['contacts', 'companies', 'tickets', 'tasks', 'time_entries', 'employees', 'communications'];
  const fieldNames = ['tags', 'skills', 'technologies', 'interests', 'certifications', 'languages'];
  
  // Generate IDs for potential entity instances (using common ID patterns)
  // Reduced from 100 to 20 for performance: Mantine's useModalsStack has O(n²) init and O(n) per state update
  for (let i = 0; i < 20; i++) {
    for (const entity of entities) {
      for (const field of fieldNames) {
        dynamicIds.push(`list-editor-${entity}-${i}-${field}`);
      }
    }
  }
  
  return dynamicIds;
};

/**
 * Inner provider that uses useUrlSync (must be inside Suspense so useSearchParams is allowed).
 * Used as the Suspense child; fallback uses GlobalModalStackProviderWithNoOpUrlSync.
 */
const GlobalModalStackProviderWithUrlSync: React.FC<{
  children: React.ReactNode;
  modalStack: ReturnType<typeof useModalsStack>;
  dataStore: ReturnType<typeof useModalDataStore>;
  syncWithUrl: boolean;
  urlParamName: string;
  defaultReplace: boolean;
}> = ({
  children,
  modalStack,
  dataStore,
  syncWithUrl,
  urlParamName,
  defaultReplace,
}) => {
  const urlSync = useUrlSync({
    syncWithUrl,
    urlParamName,
    defaultReplace,
    modalStack,
  });
  return (
    <GlobalModalStackProviderInner
      modalStack={modalStack}
      dataStore={dataStore}
      urlSync={urlSync}
      urlParamName={urlParamName}
      defaultReplace={defaultReplace}
    >
      {children}
    </GlobalModalStackProviderInner>
  );
};

/**
 * Renders context and Modal.Stack from modal stack, data store, and url sync.
 * Shared by both the Suspense fallback (with no-op url sync) and the real tree (with useUrlSync).
 */
function GlobalModalStackProviderInner({
  children,
  modalStack,
  dataStore,
  urlSync,
  urlParamName,
  defaultReplace = false,
}: {
  children: React.ReactNode;
  modalStack: ReturnType<typeof useModalsStack>;
  dataStore: ReturnType<typeof useModalDataStore>;
  urlSync: import("./hooks/useUrlSync").UrlSyncReturn;
  urlParamName: string;
  defaultReplace?: boolean;
}) {
  const { openModal, closeModal, closeAllModals } = useModalActions({
    modalStack,
    dataStore,
    urlSync,
    defaultReplace,
  });

  const modalStackRef = React.useRef(modalStack);
  React.useEffect(() => {
    modalStackRef.current = modalStack;
  }, [modalStack]);

  const isModalOpen = React.useCallback((id: GlobalModalId) => {
    return modalStack.state[id] || false;
  }, [modalStack]);

  const getModalUrl = React.useCallback((id: GlobalModalId) => {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    params.set(urlParamName, id);
    return `${typeof window !== "undefined" ? window.location.pathname : ""}?${params.toString()}`;
  }, [urlParamName]);

  const getModalData = React.useCallback((id: GlobalModalId) => {
    return dataStore.getModalData(id);
  }, [dataStore]);

  const contextValue = React.useMemo<GlobalModalStackContextType>(
    () => ({
      modalStack,
      openModal,
      closeModal,
      closeAllModals,
      isModalOpen,
      getModalUrl,
      getModalData,
      modalDataRevision: dataStore.modalDataRevision,
    }),
    [
      modalStack,
      openModal,
      closeModal,
      closeAllModals,
      isModalOpen,
      getModalUrl,
      getModalData,
      dataStore.modalDataRevision,
    ]
  );

  return (
    <GlobalModalStackContext.Provider value={contextValue}>
      <Modal.Stack>{children}</Modal.Stack>
    </GlobalModalStackContext.Provider>
  );
}

export const GlobalModalStackProvider: React.FC<GlobalModalStackProviderProps> = ({
  children,
  syncWithUrl = false,
  urlParamName = "modal",
  defaultReplace = false,
}) => {
  const allModalIds = React.useMemo(() => {
    return [...GLOBAL_MODAL_IDS, ...generateDynamicModalIds()];
  }, []);

  const modalStack = useModalsStack(allModalIds);
  const dataStore = useModalDataStore();

  // Fallback: same UI but URL sync is no-op (avoids useSearchParams during static generation).
  const fallback = (
    <GlobalModalStackProviderInner
      modalStack={modalStack}
      dataStore={dataStore}
      urlSync={NO_OP_URL_SYNC}
      urlParamName={urlParamName}
      defaultReplace={defaultReplace}
    >
      {children}
    </GlobalModalStackProviderInner>
  );

  return (
    <React.Suspense fallback={fallback}>
      <GlobalModalStackProviderWithUrlSync
        modalStack={modalStack}
        dataStore={dataStore}
        syncWithUrl={syncWithUrl}
        urlParamName={urlParamName}
        defaultReplace={defaultReplace}
      >
        {children}
      </GlobalModalStackProviderWithUrlSync>
    </React.Suspense>
  );
};
